// ─────────────────────────────────────────────────────────────────────────────
// Canonical WhatsApp-event taxonomy + relevance predicate.
//
// Shared (via the bt-common submodule) between the extension client
// (whatsapp-scheduler) and the backend dispatcher (blueticks-api). This is the
// ONLY place that knows which consumer — customer webhooks, active bots, or an
// open team-inbox tab — cares about which WhatsApp event. The client uses
// getInterestedConsumers() to decide whether to send the event AT ALL; the
// server uses getWebhookEventNameForType() to fan a received event out to the
// webhook bus.
//
// Webhook event names are stable public identifiers — never rename them.
// ─────────────────────────────────────────────────────────────────────────────

import { z } from 'zod';
import { wireIdentityOf, type WireIdentitySpec } from '../contract/wire-identity';
import * as P from './wa-event-payloads';
import type { ReceivedOn } from '../engine/sender-binding';

/** Envelope protocol version. Envelopes without v===2 are legacy (old
 *  extensions / Baileys pods) and must be handled exactly as before. */
export const UNIFIED_WA_EVENT_VERSION = 2;

export interface WaEventDescriptor {
  /** Canonical `type` on the wa-message-listener-event envelope. */
  type: string;
  /** Public webhook event name; null = not webhook-exposed. */
  webhookEventName: string | null;
  /** Team inbox consumes this event over MQTT downstream. */
  teamInboxRelevant: boolean;
  /** Active bots can be triggered by this event. */
  botRelevant: boolean;
  /** The payload (`msg`) schema — its identity spec is derived from it. */
  payloadSchema: z.ZodTypeAny;
}

// One row per event the extension can observe. Flags mirror the lanes each
// event uses today; flipping a flag is how a lane gains/loses an event.
export const WA_EVENT_DESCRIPTORS = [
  // Listener-lane events (Team Inbox realtime). receivedNewMessage doubles as
  // the webhook + bot source; its msg IS the enriched webhook payload.
  { type: 'receivedNewMessage',             webhookEventName: 'new_message_received_webhook', teamInboxRelevant: true,  botRelevant: true,  payloadSchema: P.WaListenerMessageSchema },
  { type: 'received_reaction_added',        webhookEventName: 'message_reaction_webhook',     teamInboxRelevant: true,  botRelevant: false, payloadSchema: P.WaMessageReactionWebhookSchema },
  { type: 'receivedMessageAckUpdate',       webhookEventName: null,                           teamInboxRelevant: true,  botRelevant: false, payloadSchema: P.WaMessageAckUpdatePayloadSchema },
  { type: 'receivedMessageRemoved',         webhookEventName: null,                           teamInboxRelevant: true,  botRelevant: false, payloadSchema: P.WaMessageRemovedPayloadSchema },
  { type: 'receivedMessageRevoked',         webhookEventName: null,                           teamInboxRelevant: true,  botRelevant: false, payloadSchema: P.WaListenerRevokedMessageSchema },
  { type: 'receivedCiphertextMessageAdded', webhookEventName: null,                           teamInboxRelevant: true,  botRelevant: false, payloadSchema: P.WaListenerRevokedMessageSchema },
  { type: 'receivedUnreadCountChanged',     webhookEventName: null,                           teamInboxRelevant: true,  botRelevant: false, payloadSchema: P.WaUnreadCountChangedPayloadSchema },
  // Webhook-only events: the page layer emits a dedicated enriched payload for
  // each; canonical type === webhook event name (self-describing wire format).
  { type: 'ack_changed_webhook',                     webhookEventName: 'ack_changed_webhook',                     teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaAckChangedWebhookSchema },
  { type: 'message_deleted_revoked_webhook',         webhookEventName: 'message_deleted_revoked_webhook',         teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaMessageDeletedRevokedWebhookSchema },
  { type: 'message_edited_webhook',                  webhookEventName: 'message_edited_webhook',                  teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaMessageEditedWebhookSchema },
  { type: 'poll_vote_webhook',                       webhookEventName: 'poll_vote_webhook',                       teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaPollVoteWebhookSchema },
  { type: 'reply_to_my_message_webhook',             webhookEventName: 'reply_to_my_message_webhook',             teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaReplyToMyMessageWebhookSchema },
  { type: 'participant_joined_via_link_webhook',     webhookEventName: 'participant_joined_via_link_webhook',     teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaParticipantJoinedViaLinkWebhookSchema },
  { type: 'participant_added_by_admin_webhook',      webhookEventName: 'participant_added_by_admin_webhook',      teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaParticipantAddedByAdminWebhookSchema },
  { type: 'participant_left_group_webhook',          webhookEventName: 'participant_left_group_webhook',          teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaParticipantLeftGroupWebhookSchema },
  { type: 'participant_kicked_from_group_webhook',   webhookEventName: 'participant_kicked_from_group_webhook',   teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaParticipantKickedFromGroupWebhookSchema },
  { type: 'group_admin_changed_webhook',             webhookEventName: 'group_admin_changed_webhook',             teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaGroupAdminChangedWebhookSchema },
  { type: 'group_name_changed_webhook',              webhookEventName: 'group_name_changed_webhook',              teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaGroupNameChangedWebhookSchema },
  { type: 'group_description_changed_webhook',       webhookEventName: 'group_description_changed_webhook',       teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaGroupDescriptionChangedWebhookSchema },
  { type: 'group_message_pinned_webhook',            webhookEventName: 'group_message_pinned_webhook',            teamInboxRelevant: false, botRelevant: false, payloadSchema: P.WaGroupMessagePinnedWebhookSchema },
] as const satisfies readonly WaEventDescriptor[];

/** Canonical event type — one of the taxonomy rows above. */
export type WaEventType = (typeof WA_EVENT_DESCRIPTORS)[number]['type'];

const WA_EVENT_IDENTITY = Object.fromEntries(
  WA_EVENT_DESCRIPTORS.map((d) => [d.type, wireIdentityOf(d.payloadSchema)]),
) as Record<WaEventType, WireIdentitySpec>;

/** The identity spec of an event type's payload — derived once at load from
 *  its payloadSchema. The engine's single emit maps exactly these fields. */
export function getWaEventIdentity(type: WaEventType): WireIdentitySpec {
  return WA_EVENT_IDENTITY[type];
}

const DESCRIPTOR_BY_TYPE: ReadonlyMap<string, WaEventDescriptor> = new Map(
  WA_EVENT_DESCRIPTORS.map((d) => [d.type, d]),
);

/**
 * Opt-in webhook event for the account owner's OWN outgoing messages
 * (`receivedNewMessage` with `fromMe === true`). Separate from
 * `new_message_received_webhook` (inbound-only by contract, see
 * `getWebhookEventNameForType`) — a customer must explicitly subscribe a
 * webhook to THIS name to receive their own sent messages. Never folded into
 * the inbound event: that would silently change behavior for every existing
 * webhook already subscribed to `new_message_received_webhook`.
 */
export const RECEIVED_NEW_MESSAGE_FROM_ME_WEBHOOK = 'new_message_received_from_me_webhook';

export interface WaEventRelevanceInput {
  type: string;
  /** From the message payload; gates the inbound-only webhook + bot lanes. */
  fromMe?: boolean;
  /**
   * The message's chat is a btgpt-linked "AI Conversation" group (client-side
   * membership check against the synced linked-chat set, AFTER the hidden-
   * marker echo guard). Opens the aiConversation lane, which accepts fromMe
   * messages by default because in a solo linked group the user's own
   * messages ARE the input. (teamInbox is also unconditional on fromMe — it
   * never gates on it at all, not even to opt in — so it passes fromMe
   * messages through too. The opt-in webhook event above, and
   * `RelevanceContext.mqttIncludeOwnMessages` below, are additional,
   * explicitly-enabled lanes for fromMe — see each for its own gate.)
   */
  aiLinkedChat?: boolean;
}

export interface RelevanceContext {
  /** Union of `events` across the user's enabled, non-deleted webhooks. */
  subscribedWebhookEvents: string[];
  /** Bot ids whose trigger scope matched the sender (client-computed). */
  matchingBotIds: string[];
  /** A team-inbox tab is open somewhere (MQTT presence, fresh heartbeat). */
  teamInboxOpen: boolean;
  /**
   * Account-level opt-in (`User.preferences.mqttIncludeOwnMessages`):
   * publish the owner's own outgoing `receivedNewMessage` events to MQTT.
   * Independent of the webhook opt-in above — a customer with no webhooks at
   * all can still turn this on to receive their own messages over MQTT.
   * Defaults false: without it, fromMe messages are dropped client-side
   * exactly as before this flag existed.
   */
  mqttIncludeOwnMessages?: boolean;
  /**
   * Account-level opt-in (`User.preferences.mqttIncludeInboundMessages`):
   * publish INBOUND (non-fromMe) `receivedNewMessage` events to MQTT even
   * with no webhook configured/subscribed at all. Without this, MQTT
   * delivery of inbound messages has always been a side effect of the
   * webhook lane (see `dispatchLegacyWebhook`'s REALTIME_MESSAGES_PATH
   * publish, gated on the SAME event name a subscribed webhook needs) — a
   * customer with zero webhooks got nothing on MQTT either. This makes MQTT
   * a genuinely independent third channel, symmetric with
   * `mqttIncludeOwnMessages` for the fromMe side. Defaults false: without
   * it, inbound messages are gated by `webhooks` exactly as before this
   * flag existed.
   */
  mqttIncludeInboundMessages?: boolean;
  /**
   * Server-computed account capability (`User.claudeAgentsActive`, rev 3.3
   * §6.5): the owner has at least one Claude agent or operator chat. The
   * backend is the single enqueue point for agents and needs EVERY new
   * message, in BOTH directions — inbound (customer input) and fromMe (echo
   * resolution via messageSecret, human-intervention / takeover, operator
   * commands typed from the owner's phone). Absent/false ⇒ no change: the
   * event is gated by the other lanes exactly as before.
   */
  claudeAgentsActive?: boolean;
}

export interface InterestedConsumers {
  webhooks: boolean;
  bots: boolean;
  teamInbox: boolean;
  /** btgpt "AI Conversation" bridge (linked WA group → headless btgpt turn). */
  aiConversation: boolean;
  /** `RelevanceContext.mqttIncludeOwnMessages` opted a fromMe message in. */
  mqttOwnMessages: boolean;
  /** `RelevanceContext.mqttIncludeInboundMessages` opted an inbound message in
   *  independent of any webhook subscription. */
  mqttInboundMessages: boolean;
  /** `RelevanceContext.claudeAgentsActive` opted a new message (either
   *  direction) in for the Claude-agents ingress (rev 3.3 §6.5). */
  claudeAgents: boolean;
  /** True if ANY consumer is interested — the client's send/skip decision. */
  any: boolean;
}

/**
 * Webhook event name for a canonical type, or null when the event must not
 * reach customer webhooks. `new_message_received_webhook` is inbound-only by
 * contract — a fromMe `receivedNewMessage` maps instead to the separate
 * opt-in event `RECEIVED_NEW_MESSAGE_FROM_ME_WEBHOOK`, which only fires for a
 * webhook that explicitly subscribed to it.
 */
export const getWebhookEventNameForType = (
  type: string,
  fromMe: boolean,
): string | null => {
  const d = DESCRIPTOR_BY_TYPE.get(type);
  if (!d || !d.webhookEventName) return null;
  if (type === 'receivedNewMessage' && fromMe) return RECEIVED_NEW_MESSAGE_FROM_ME_WEBHOOK;
  return d.webhookEventName;
};

/**
 * The single relevance decision, shared by client and server.
 *
 * Unknown event types FAIL OPEN (any=true): a type added to the page layer
 * before this taxonomy learns about it must be delivered, not silently
 * dropped. A wrong send costs one redundant request; a wrong skip loses data.
 */
export const getInterestedConsumers = (
  event: WaEventRelevanceInput,
  ctx: RelevanceContext,
): InterestedConsumers => {
  const d = DESCRIPTOR_BY_TYPE.get(event.type);
  if (!d) {
    return {
      webhooks: false,
      bots: false,
      teamInbox: false,
      aiConversation: false,
      mqttOwnMessages: false,
      mqttInboundMessages: false,
      claudeAgents: false,
      any: true,
    };
  }
  const webhookEventName = getWebhookEventNameForType(event.type, event.fromMe === true);
  const webhooks = !!webhookEventName && ctx.subscribedWebhookEvents.includes(webhookEventName);
  const bots = d.botRelevant && event.fromMe !== true && ctx.matchingBotIds.length > 0;
  const teamInbox = d.teamInboxRelevant && ctx.teamInboxOpen;
  // The bridge lane: new messages in a linked AI-conversation group. fromMe is
  // deliberately NOT gated here — the user's own messages are the input; echo
  // suppression (hidden marker) happens before this flag is set.
  const aiConversation = d.type === 'receivedNewMessage' && event.aiLinkedChat === true;
  // The account-level MQTT opt-in: publish the owner's own outgoing messages.
  // Once this makes `any` true the client sends the event upstream, and the
  // server's MQTT publish stage is unconditional — no separate server-side
  // check is needed for MQTT delivery itself, only for deciding to send at all.
  const mqttOwnMessages =
    d.type === 'receivedNewMessage' && event.fromMe === true && ctx.mqttIncludeOwnMessages === true;
  // The account-level MQTT opt-in for INBOUND messages: makes MQTT a fully
  // independent channel, not merely a side effect of a subscribed webhook.
  const mqttInboundMessages =
    d.type === 'receivedNewMessage' && event.fromMe !== true && ctx.mqttIncludeInboundMessages === true;
  // Claude agents / operator chats (rev 3.3 §6.5): every new message, BOTH
  // directions — deliberately NOT gated on fromMe (own messages carry the
  // messageSecret echo token and are the human-intervention signal).
  const claudeAgents = d.type === 'receivedNewMessage' && ctx.claudeAgentsActive === true;
  return {
    webhooks,
    bots,
    teamInbox,
    aiConversation,
    mqttOwnMessages,
    mqttInboundMessages,
    claudeAgents,
    any:
      webhooks || bots || teamInbox || aiConversation || mqttOwnMessages || mqttInboundMessages || claudeAgents,
  };
};

export interface WaEventBotDispatch {
  /** message.from with both `_serialized` and `id._serialized` overridden to
   *  the canonical @c.us JID (the form saved bot trigger contact lists use). */
  overriddenFrom: unknown;
  resolvedSenderId: string;
  originalSenderId: string;
}

/** The v2 wire envelope POSTed to /actions?cmd=wa-message-listener-event. */
export interface WaListenerEventEnvelope {
  v?: number;
  type: string;
  msg: unknown;
  /** Enriched webhook payload when it differs from msg (webhook-only events
   *  carry it in msg itself; receivedNewMessage's msg IS the payload). */
  webhookPayload?: unknown;
  /** Present when client-side bot matching found relevant bots. */
  botDispatch?: WaEventBotDispatch;
  /** Advisory — the server re-validates against Mongo. `aiConversation`,
   *  `mqttOwnMessages` and `mqttInboundMessages` say WHY an event with no
   *  webhook/bot/teamInbox match was sent anyway: a btgpt-linked AI
   *  conversation, or the account's mqttIncludeOwnMessages /
   *  mqttIncludeInboundMessages opt-ins. `claudeAgents` = the owner has a
   *  Claude agent or operator chat (rev 3.3 §6.5; both directions). */
  hints?: {
    webhooks: boolean;
    botIds: string[];
    teamInbox: boolean;
    aiConversation?: boolean;
    mqttOwnMessages?: boolean;
    mqttInboundMessages?: boolean;
    claudeAgents?: boolean;
  };
  /** Sender's address-book name, else their pushName (the pod's resolver rule,
   *  contact-display-name-resolver.ts:95). Both engines set it for
   *  receivedNewMessage. Not an identity field; distinct from msg.notifyName. */
  contactDisplayName?: string;
  /** #922 S0b: the account number + engine instance the event arrived on. Optional: pre-S0b builds
   *  omit it and the backend fills `phoneNumber` from `msg.to`. */
  receivedOn?: ReceivedOn;
}

/** Per-engine LID resolution counters. Both engines log these four field names
 *  so one query covers both. Counted with `lidOutcome` (the one rule). */
export interface LidCounters {
  lidDmResolvedTotal: number;
  lidDmUnresolvedTotal: number;
  lidGroupResolvedTotal: number;
  lidGroupUnresolvedTotal: number;
}

const jidOf = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : typeof (v as { _serialized?: unknown })?._serialized === 'string'
    ? (v as { _serialized: string })._serialized : undefined;
const isLid = (jid: string | undefined): boolean => !!jid && /@(hosted\.)?lid$/.test(jid);

/**
 * The ONE counting rule, for a receivedNewMessage payload before/after the
 * engine's wire conversion: a person DM (`@c.us`/`@lid` chat) → `id.remote`;
 * a group, `status@broadcast` or any other chat → `id.participant`. An
 * input that was @lid and came out @c.us is resolved; @lid that stayed @lid is
 * unresolved; a non-lid input → null (nothing to count).
 */
export function lidOutcome(before: unknown, after: unknown): keyof LidCounters | null {
  const idBefore = (before as { id?: { remote?: unknown; participant?: unknown } })?.id;
  const idAfter = (after as { id?: { remote?: unknown; participant?: unknown } })?.id;
  // Only a person chat (@c.us / @lid) is a DM; groups, status@broadcast and
  // every other kind count by their participant.
  const isGroup = !/@(c\.us|(hosted\.)?lid)$/.test(jidOf(idBefore?.remote) ?? '');
  const field = isGroup ? 'participant' : 'remote';
  if (!isLid(jidOf(idBefore?.[field]))) return null;
  const resolved = (jidOf(idAfter?.[field]) ?? '').endsWith('@c.us');
  if (isGroup) return resolved ? 'lidGroupResolvedTotal' : 'lidGroupUnresolvedTotal';
  return resolved ? 'lidDmResolvedTotal' : 'lidDmUnresolvedTotal';
}
